import { KFDataTable } from "@kf/ic2fx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Arrow from "../../../assets/icons/table-arrow.svg";
import { JobPricingReportConstants } from "../../../constants/JobPricingReportConstants";
import { getLanguageCode } from "../../../utilities/helper";
import { PAGE_URL } from "../../../constants/urls.constants";
import {
  JobPricingHeaderDetails,
  JobPricingReportViewBy
} from "../../../helpers/models/JobPricingDetailModels";
import { RootState } from "../../../store/reducers";
import {
  getPricingFiltersThunk,
  updateReportHeaderDetails,
  updateReportViewBy
} from "../../../store/slices/jobPricingDetailsSlice";
import { SJPRListTypes } from "../../../store/slices/jobPricingListSlice";
import { updateJobPricingPayloads } from "../../../store/slices/jobPricingSearchSlice";
import {
  JobPricingListThunk,
  resetJobPricingList,
  resetSearchKey,
  updatedSearchKey
} from "../../../store/slices/SJPR/listSlice";
import { LoadingOverlay, NoDataListScreen } from "../NoData/ListScreen";

const JobsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useNavigate();
  const [jobsList, setJobsList] = useState<any>([]);
  const { jobPricingList } = useSelector((state: RootState) => {
    return state;
  });
  const { selectedCountry } = useSelector((state: RootState) => {
    return state.country;
  });

  const getJobsList = (searchKey?: string) => {
    setJobsList([]);
    let updatedFilterPayload: any = { ...jobPricingList?.filters };
    for (const key in updatedFilterPayload) {
      if (updatedFilterPayload[key]?.length === 0) {
        delete updatedFilterPayload[key];
      }
    }
    let data: any;
    if (
      (jobPricingList?.searchKey && jobPricingList?.searchKey?.length > 0) ||
      (searchKey && searchKey?.length > 0)
    ) {
      data = {
        countryCode: selectedCountry?.key,
        filters: updatedFilterPayload || {},
        language:
          getLanguageCode(),
        search: jobPricingList?.searchKey || searchKey || ""
      };
    } else {
      data = {
        countryCode: selectedCountry?.key,
        filters: updatedFilterPayload || {},
        language:
          getLanguageCode()
      };
    }

    if (jobPricingList?.peerGroupId) {
      data.peerGroupId = jobPricingList.peerGroupId;
    }

    const payLoad = {
      category: SJPRListTypes.JOBS,
      data
    };
    dispatch(getPricingFiltersThunk(data));
    dispatch(JobPricingListThunk(payLoad));
  };
  useEffect(() => {
    const stringSearchKey = sessionStorage.getItem("searchKey") || "";
    stringSearchKey ? getJobsList(JSON.parse(stringSearchKey)) : getJobsList();
    return () => {
      dispatch(resetJobPricingList());
    };
  }, []);

  useEffect(() => {
    if (jobPricingList?.list?.jobs) {
      setJobsList(
        jobPricingList?.list?.jobs?.map((item: any) => {
          return {
            ...item,
            id: item?.jobKey
          };
        })
      );
    } else {
      setJobsList([]);
    }
  }, [jobPricingList?.list?.jobs]);

  const redirectToReportPricingDetails = (item: any) => {
    dispatch(resetSearchKey());
    sessionStorage.removeItem("searchKey");
    dispatch(updateReportViewBy(JobPricingReportViewBy.JOB));
    const updatedPricingPayloads = {
      searchCategory: JobPricingReportViewBy.JOB,
      jobKey: (item?.jobKey || "ALL").toString(),
      jobOrgCode: (item?.jobOrgCode || "ALL").toString(),
      jobName: item?.jobName || "ALL",
      jobDescription: item?.jobDescription || "",
      jobFamilyName: item?.jobFamilyName || "ALL",
      jobSubFamilyName: item?.jobSubFamilyName || "ALL",
      marketJobFamily: (item?.jobFamilyKey || "ALL").toString(),
      marketJobSubFamily: (item?.jobSubFamilyKey || "ALL").toString(),
      totalJobs: "0",
      jobsInOrg: "0",
      gradeId: (item?.grade || "ALL").toString(),
      gradeSpan: "ALL",
      searchTerm: item?.jobName || "",
      peerGroupKey: (jobPricingList?.peerGroupId || "ALL").toString()
    };
    const pricingHeaderDetails = {
      title: item?.jobName || "",
      description: item?.jobDescription || "",
      function: item?.jobFamilyName || "-",
      grade: item?.grade || "-",
      jobCode: item?.jobOrgCode || "0",
      subFunction: item?.jobSubFamilyName || "-"
    } as JobPricingHeaderDetails;
    dispatch(updateJobPricingPayloads(updatedPricingPayloads));
    dispatch(updateReportHeaderDetails(pricingHeaderDetails));
    history(PAGE_URL.JOB_PRICING_DETAILS);
  };

  const JobListColumns: any = [
    {
      field: "jobName",
      headerName: t("pa.jobName"),
      flex: 4,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "grade",
      headerName: t("pa.grade"),
      sortable: false,
      disableColumnMenu: true,
      flex: 2
    },
    {
      field: "jobFamilyName",
      headerName: t("pa.jobFunctionTitle"),
      sortable: false,
      disableColumnMenu: true,
      flex: 2
    },
    {
      field: "incumbentCount",
      headerName: t("pa.sjpr.incumbents"),
      sortable: false,
      disableColumnMenu: true,
      flex: 2
    },
    {
      field: "",
      headerName: " ",
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: () => {
        return (
          <div>
            <img src={Arrow} alt="Details icon" />
          </div>
        );
      }
    }
  ];

  return (
    <div
      className={`app-table ${
        jobPricingList?.isListloading || jobsList.length === 0
          ? "grid-overlay-height"
          : ""
      }`}
    >
      <KFDataTable
        sx={{
          "& .MuiDataGrid-cell:hover": {
            cursor: "pointer"
          },
          "& .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary:focus, & .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary:hover, & .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary, & .kfic2-button.MuiIconButton-root.kfic2-ghostSecondary:active ":
            {
              color: "var(--button-text)",
              borderColor: "var(--button-text)"
            },
          "& .css-i4bv87-MuiSvgIcon-root": {
            fill: "var(--button-text)"
          }
        }}
        paginationLabel={{
          "Items per page:": t("pa.pagination.itemsPerPage"),
          of: t("pa.pagination.of"),
          items: t("pa.pagination.items"),
          "more than": t("pa.pagination.moreThan"),
          page: t("pa.pagination.page"),
          pages: t("pa.pagination.pages")
        }}
        rowsPerPageOptions={
          JobPricingReportConstants.TABLE_ROWS_PER_PAGE_OPTIONS
        }
        sortingOrder={JobPricingReportConstants.TABLE_SORTING_ORDER}
        rows={jobsList}
        columns={JobListColumns}
        loading={jobPricingList?.isListloading}
        onCellClick={(params: any, event, details) => {
          redirectToReportPricingDetails(params.row);
        }}
        initialState={{
          pagination: {
            pageSize: JobPricingReportConstants.TABLE_DEFAULT_PAGE_SIZE
          }
        }}
        components={{
          NoRowsOverlay: () => <NoDataListScreen />,
          LoadingOverlay: () => <LoadingOverlay columsList={JobListColumns} />
        }}
      />
    </div>
  );
};

export default JobsList;
